'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatActionButton } from '../chat-action-button';
import { PromoMessage } from './message';
import { PromoActions } from './actions';
import { prepareSalebotLink } from '@/features/free-interview/actions';
export function PromoTelegram({
	interviewId,
	courseId,
	onLinked,
	track,
	introduction,
}: {
	interviewId: string;
	courseId: string;
	onLinked: () => void;
	track: (event: string, params?: Record<string, string>) => void;
	introduction: string;
}) {
	const [busy, setBusy] = useState(false),
		[error, setError] = useState(''),
		[link, setLink] = useState(''),
		[expires, setExpires] = useState(0);
	const [checked, setChecked] = useState(false);
	const locked = useRef(false),
		alive = useRef(true),
		done = useRef(false);
	const check = useCallback(
		async (create = false) => {
			if (locked.current || done.current) return;
			locked.current = true;
			setBusy(true);
			const popup = create ? window.open('about:blank', '_blank') : null;
			if (popup) popup.opener = null;
			try {
				const r = await prepareSalebotLink(interviewId, courseId, create);
				if (!alive.current) {
					popup?.close();
					return;
				}
				if (!r.data) {
					popup?.close();
					setError(r.error);
					track('result', { action: 'telegram_check', outcome: 'error' });
					return;
				}
				setError('');
				setChecked(true);
				if (r.data.linked) {
					popup?.close();
					done.current = true;
					track('result', { action: 'telegram_link', outcome: 'success' });
					onLinked();
					return;
				}
				if (r.data.deepLink) {
					setLink(r.data.deepLink);
					setExpires(Date.parse(r.data.expiresAt ?? ''));
					if (popup) popup.location.href = r.data.deepLink;
				} else popup?.close();
				if (!create && link)
					setError(
						'Привязка пока не подтверждена. Открой бота и нажми «Запустить».',
					);
			} catch {
				popup?.close();
				if (alive.current)
					setError('Нет соединения. Попробуй проверить привязку ещё раз.');
				track('result', { action: 'telegram_check', outcome: 'network_error' });
			} finally {
				locked.current = false;
				if (alive.current) setBusy(false);
			}
		},
		[courseId, interviewId, onLinked, track, link],
	);
	const checkRef = useRef(check);
	checkRef.current = check;
	useEffect(() => {
		alive.current = true;
		const t = setTimeout(() => void checkRef.current(), 0);
		return () => {
			alive.current = false;
			clearTimeout(t);
		};
	}, []);
	useEffect(() => {
		if (!expires) return;
		const timer = setInterval(() => {
			if (Date.now() >= expires) {
				setExpires(0);
				setLink('');
				setError('Ссылка истекла. Запроси новую.');
				track('result', { action: 'telegram_link', outcome: 'expired' });
			} else void checkRef.current();
		}, 3000);
		return () => clearInterval(timer);
	}, [expires, track]);
	return (
		<PromoMessage
			key={checked ? 'link' : 'checking'}
			text={checked ? introduction : 'Проверяем привязку Telegram…'}
		>
			<div className="space-y-3.5">
				{(checked || error) && (
					<PromoActions>
						<ChatActionButton
							variant="white"
							disabled={busy}
							onClick={() => {
								if (link && Date.now() < expires) {
									track('action', { action: 'telegram_open' });
									window.open(link, '_blank', 'noopener,noreferrer');
									return;
								}
								track('action', { action: 'telegram_link' });
								void check(true);
							}}
						>
							Запустить Telegram бота
						</ChatActionButton>
						<ChatActionButton variant="white" disabled>
							Запустить MAX бота
						</ChatActionButton>
						<ChatActionButton variant="white" disabled>
							Запустить ВК бота
						</ChatActionButton>
					</PromoActions>
				)}
				{error && (
					<p role="alert" className="text-sm text-red">
						{error}
					</p>
				)}
			</div>
		</PromoMessage>
	);
}
